import * as THREE from "three";
import { editorStore } from "../../store/editorStore";
import { floorplannerStore } from "../../store/floorplannerStore";
import { WallEdgeConnection, WallType } from "../../types/floorplannerTypes";
import { EdgePositionState } from "./applyAlignmentAfterSnap";
import { calculateWallEdgePoint, getWallAngle } from "../../utils/edgeConnectionHelpers";

type PivotAnchorCache = {
    primaryAnchor: THREE.Vector2;
    alternateAnchor?: THREE.Vector2;
    currentAnchor: "primary" | "alternate";
    initialSideSign: 1 | -1;
    currentSideSign: 1 | -1;
    targetAngle: number;
    lastRelativeSign?: 1 | -1 | 0;
    primaryIsLeft: boolean;
    /** Tracks hysteresis distance for colinear switching to prevent flicker */
    hysteresisDistance?: number;
};

const normalizeAngle = (angle: number): number => {
    let a = angle;
    while (a > Math.PI) a -= 2 * Math.PI;
    while (a < -Math.PI) a += 2 * Math.PI;
    return a;
};

/**
 * Minimum distance the wall must travel past the colinear angle before switching corners.
 * This prevents flickering when hovering near the colinear threshold.
 */
const COLINEAR_HYSTERESIS_DISTANCE = 0.05; // meters

/**
 * Adjusts wall endpoint positions so rotation pivots around a corner instead of the center.
 * 
 * === PIVOT ANCHOR RULES ===
 * These rules MUST be followed to prevent jumping during drag operations:
 * 
 * 1. ALT/OPTION MODE:
 *    - Anchor point should NEVER be adjusted if in ALT/OPTION center connection mode.
 *    - Return positions unchanged when ALT is pressed.
 * 
 * 2. SOURCE WALL ANCHOR POINT:
 *    - Is always one of the TWO source wall endpoint corners of the connected end.
 *    - Specifically: the corner that was touching the target wall BEFORE drag started.
 *    - This is determined by the `sourceEdgePoint` in the original edge connection.
 * 
 * 3. TARGET WALL ANCHOR POINT:
 *    - Will always be ON THE OUTLINE of the target wall.
 *    - For endpoint-to-endpoint connections (start or end), this is the corner that was touching the target wall BEFORE drag started.
 *    - For mid-wall connections (anchorConnection.targetEndpoint === "middle"), the anchor point is the mid-point of the target wall that is on the border of the outline.
 *    - This is the `targetEdgePoint` from the original edge connection.
 * 
 * 4. ENDPOINT-TO-ENDPOINT CONNECTIONS (corner-to-corner):
 *    - When source wall is connected to target wall's endpoint corners (start or end),
 *      it should ALWAYS maintain the anchor point to the target wall corner of that connection.
 *    - The `primaryAnchor` is set to `connection.targetEdgePoint`.
 * 
 * 5. MID-WALL CONNECTIONS (T-junctions):
 *    - When source wall is connected to target wall mid-section (not endpoint corners),
 *      the anchor point should always be the target wall outline position of the connection.
 * 
 * 6. CORNER FLIPPING (colinear transition):
 *    - At certain angle when the source wall is colinear with the target wall, 
 *      the anchor point could flip to the OTHER source wall corner.
 *    - This maintains smooth hysteresis drag operation at the transition point
 *      when both source corner points touch the target outline exactly.
 *    - Use COLINEAR_HYSTERESIS_DISTANCE to prevent flickering.
 * 
 * 7. INITIAL ANCHOR DETERMINATION:
 *    - BEFORE a drag operation, it is the CONNECTED CORNER that dictates
 *      which source and target edge-to-edge corner is the current anchored one.
 *    - This is captured in `originalEdgeConnections` when beginWallDrag is called.
 * 
 * If these rules are upheld, NO jumping should EVER happen during drag operation.
 * 
 * === ALGORITHM ===
 * 1. Get the `primaryAnchor` from the edge connection's `targetEdgePoint`
 * 2. Get the `sideSign` (1 or -1) indicating which side of the wall centerline the anchor corner is
 * 3. Calculate: centerline_position = anchorPoint - (perpendicular * halfWidth * sideSign)
 * 4. The sideSign MUST match the original connection's sourceEdgePoint offset from centerline
 */
export const applyWallPivot = (
    wall: WallType,
    positions: EdgePositionState,
    draggedEndpoint: "start" | "end",
): EdgePositionState => {
    // RULE 1: ALT/OPTION explicitly disables anchored pivoting
    if (editorStore.isAltPressed) {
        return positions;
    }


    const dragState = floorplannerStore.wallDragState.get(wall.id);
    const anchorEndpoint: "start" | "end" = draggedEndpoint === "start" ? "end" : "start";
    const pivotAnchors = (dragState as any)?.pivotAnchors;

    const ensurePivotCache = (): PivotAnchorCache | undefined => {
        const existing = pivotAnchors?.[anchorEndpoint];


        if (existing) {
            return existing;
        }

        const anchorConnection: WallEdgeConnection | undefined = dragState?.originalEdgeConnections?.find(
            (conn) => conn?.sourceEndpoint === anchorEndpoint && conn?.sourceWallId === wall.id,
        );
        if (!anchorConnection) {
            return undefined;
        }

        const targetWall = floorplannerStore.findObjectId(anchorConnection.targetWallId) as WallType | undefined;
        if (!targetWall || targetWall.type !== "wall") {
            return undefined;
        }

        const fixedStart = dragState?.fixedEndpoints?.start ?? wall.start;
        const fixedEnd = dragState?.fixedEndpoints?.end ?? wall.end;
        const sourceAngle = Math.atan2(fixedEnd.y - fixedStart.y, fixedEnd.x - fixedStart.x);
        const targetAngle = getWallAngle(targetWall);

        // Calculate source wall direction and perpendicular for corner calculations
        const sourceEndpointPos = anchorEndpoint === "start" ? fixedStart : fixedEnd;
        const sourceDir = new THREE.Vector2().subVectors(fixedEnd, fixedStart);
        const sourceLen = sourceDir.length();
        if (sourceLen < 1e-6) {
            return undefined;
        }
        const sourcePerp = new THREE.Vector2(-sourceDir.y / sourceLen, sourceDir.x / sourceLen);
        const sourceHalfWidth = (wall.wallWidth || floorplannerStore.wallWidth) / 2;
        const sourceCornerLeft = sourceEndpointPos.clone().add(sourcePerp.clone().multiplyScalar(sourceHalfWidth));
        const sourceCornerRight = sourceEndpointPos.clone().sub(sourcePerp.clone().multiplyScalar(sourceHalfWidth));

        // Determine anchor and sideSign based on connection type
        let primaryAnchor: THREE.Vector2;
        let alternateAnchor: THREE.Vector2 | undefined;
        let primaryIsLeft: boolean;
        let initialSideSign: 1 | -1;

        // === MID-POINT CONNECTION HANDLING ===
        // For T-junctions (targetEndpoint === "middle"), the anchor is the SOURCE wall's
        // corner that was touching the target wall's outline. This corner should remain
        // fixed during drag - the wall pivots around it.
        if (anchorConnection.targetEndpoint === "middle") {
            // Find which source corner was touching the target wall
            // by comparing to the targetEdgePoint from the connection
            const distLeft = anchorConnection.targetEdgePoint.distanceTo(sourceCornerLeft);
            const distRight = anchorConnection.targetEdgePoint.distanceTo(sourceCornerRight);

            if (distLeft <= distRight) {
                // Source's LEFT corner was touching the target wall - use it as anchor
                primaryAnchor = sourceCornerLeft.clone();
                initialSideSign = 1;
                primaryIsLeft = true;
            } else {
                // Source's RIGHT corner was touching the target wall - use it as anchor
                primaryAnchor = sourceCornerRight.clone();
                initialSideSign = -1;
                primaryIsLeft = false;
            }

            // No alternate anchor for mid-point connections - we don't flip corners
            alternateAnchor = undefined;
        } else {
            // === CORNER-TO-CORNER CONNECTION HANDLING ===
            // For endpoint connections (start/end), use the corner-based anchor logic

            // Determine which endpoint of the target wall the source wall is actually connected to.
            // DON'T trust anchorConnection.targetEndpoint as it may be incorrectly stored.
            // Instead, calculate based on which target endpoint is geometrically closest to the source.
            const distToTargetStart = sourceEndpointPos.distanceTo(targetWall.start);
            const distToTargetEnd = sourceEndpointPos.distanceTo(targetWall.end);

            // Use the geometrically determined endpoint, not the stored connection value
            const targetEndpoint: "start" | "end" = distToTargetStart <= distToTargetEnd ? "start" : "end";

            // Get both corners of the target wall at the determined connection endpoint
            // These are the ACTUAL corner positions calculated from the target wall's geometry
            const edgeLeft = calculateWallEdgePoint(targetWall, targetEndpoint, sourceAngle, undefined, undefined, "left").edgePosition;
            const edgeRight = calculateWallEdgePoint(targetWall, targetEndpoint, sourceAngle, undefined, undefined, "right").edgePosition;

            // CRITICAL: Find which source corner is closest to which target corner
            // This is the GROUND TRUTH for the connection - actual geometry, not edge connection data
            // The closest corner pair determines which corners were touching before drag started
            const distLeftLeft = edgeLeft.distanceTo(sourceCornerLeft);
            const distLeftRight = edgeLeft.distanceTo(sourceCornerRight);
            const distRightLeft = edgeRight.distanceTo(sourceCornerLeft);
            const distRightRight = edgeRight.distanceTo(sourceCornerRight);

            const minDist = Math.min(distLeftLeft, distLeftRight, distRightLeft, distRightRight);

            if (minDist === distLeftLeft) {
                // Target's LEFT corner connected to Source's LEFT corner
                primaryAnchor = edgeLeft.clone();
                alternateAnchor = edgeRight.clone();
                primaryIsLeft = true;
                initialSideSign = 1; // Use source's left corner
            } else if (minDist === distLeftRight) {
                // Target's LEFT corner connected to Source's RIGHT corner
                primaryAnchor = edgeLeft.clone();
                alternateAnchor = edgeRight.clone();
                primaryIsLeft = true;
                initialSideSign = -1; // Use source's right corner
            } else if (minDist === distRightLeft) {
                // Target's RIGHT corner connected to Source's LEFT corner
                primaryAnchor = edgeRight.clone();
                alternateAnchor = edgeLeft.clone();
                primaryIsLeft = false;
                initialSideSign = 1; // Use source's left corner
            } else {
                // Target's RIGHT corner connected to Source's RIGHT corner
                primaryAnchor = edgeRight.clone();
                alternateAnchor = edgeLeft.clone();
                primaryIsLeft = false;
                initialSideSign = -1; // Use source's right corner
            }
        }



        const cache: PivotAnchorCache = {
            primaryAnchor,
            alternateAnchor,
            currentAnchor: "primary",
            initialSideSign,
            currentSideSign: initialSideSign,
            targetAngle,
            // Initialize lastRelativeSign to initialSideSign so that when walls start colinear
            // and you drag to the opposite side, the switching condition will be satisfied.
            // (If lastRelativeSign was 0, the condition "lastSign !== 0" would fail)
            lastRelativeSign: initialSideSign,
            primaryIsLeft,
        };

        if (pivotAnchors) {
            pivotAnchors[anchorEndpoint] = cache;
        }
        return cache;
    };

    const pivotCache: PivotAnchorCache | undefined = ensurePivotCache();
    if (!pivotCache) {
        return positions;
    }

    const halfWidth = (wall.wallWidth || floorplannerStore.wallWidth) / 2;
    if (!isFinite(halfWidth) || halfWidth <= 0) {
        return positions;
    }

    // Keep the dragged endpoint fixed at the cursor while solving for the anchored endpoint
    const draggedPosition = draggedEndpoint === "start" ? positions.start.clone() : positions.end.clone();
    const nextPositions: EdgePositionState = {
        start: positions.start.clone(),
        end: positions.end.clone(),
    };

    const maxIterations = 6;
    const convergenceEpsilon = 1e-7;

    for (let i = 0; i < maxIterations; i++) {
        // Keep the dragged endpoint locked to the cursor
        if (draggedEndpoint === "start") {
            nextPositions.start.copy(draggedPosition);
        } else {
            nextPositions.end.copy(draggedPosition);
        }

        const direction = new THREE.Vector2().subVectors(nextPositions.end, nextPositions.start);
        const lengthSq = direction.lengthSq();
        if (lengthSq < 1e-9) {
            return positions;
        }


        const directionNorm = direction.clone().normalize();

        // Calculate the current wall angle
        const currentAngle = Math.atan2(direction.y, direction.x);

        // Detect if we've crossed the colinear threshold with the target wall.
        // When the wall becomes colinear with the target wall (same angle or 180° opposite),
        // we need to switch which corner we pivot around to maintain proper geometry.
        //
        // The relative angle tells us which side of the target wall angle we're on.
        // When this sign changes, we've crossed the colinear threshold.
        const targetAngle = pivotCache.targetAngle;

        // Calculate the signed angle difference between current wall and target wall
        // This tells us if we're "above" or "below" the colinear line
        const angleDiff = normalizeAngle(currentAngle - targetAngle);

        // Determine which "side" of the target angle we're on
        // We consider both parallel (0°) and anti-parallel (180°) as colinear boundaries
        const parallelThreshold = Math.PI / 90; // ~2 degrees - angle zone considered "colinear"
        const isNearColinear = Math.abs(angleDiff) < parallelThreshold ||
            Math.abs(Math.abs(angleDiff) - Math.PI) < parallelThreshold;

        // For colinear corner switching, we need a different approach than checking
        // which side of a line the dragged point is on. Instead, we calculate which
        // SOURCE corner is geometrically closer to the TARGET wall's centerline.
        // 
        // When walls are colinear and connected at TOP corners, the TOP corners are on/near
        // the target's centerline. When you rotate the source wall, eventually the BOTTOM
        // corners become closer to the target's centerline - that's when we should flip.

        // Calculate the target wall's centerline as a line through the anchor point
        const targetDir = new THREE.Vector2(Math.cos(targetAngle), Math.sin(targetAngle));
        const targetPerp = new THREE.Vector2(-targetDir.y, targetDir.x);

        // Get the current anchor point (on the target wall)
        const currentAnchorPoint = pivotCache.currentAnchor === "primary"
            ? pivotCache.primaryAnchor
            : (pivotCache.alternateAnchor ?? pivotCache.primaryAnchor);

        // Calculate the current source corners based on the CURRENT wall angle
        const currentPerp = new THREE.Vector2(-directionNorm.y, directionNorm.x);
        const anchoredCenter = anchorEndpoint === "start" ? nextPositions.start : nextPositions.end;
        const currentCornerLeft = anchoredCenter.clone().add(currentPerp.clone().multiplyScalar(halfWidth));
        const currentCornerRight = anchoredCenter.clone().sub(currentPerp.clone().multiplyScalar(halfWidth));

        // Calculate perpendicular distance from each current corner to the target's centerline
        // The centerline passes through currentAnchorPoint with direction targetDir
        const leftToAnchor = currentCornerLeft.clone().sub(currentAnchorPoint);
        const rightToAnchor = currentCornerRight.clone().sub(currentAnchorPoint);

        // Perpendicular distance (signed) from each corner to the target centerline
        const distLeftToLine = leftToAnchor.dot(targetPerp);
        const distRightToLine = rightToAnchor.dot(targetPerp);

        // Which corner is closer to the centerline?
        // If |distLeft| < |distRight|, the LEFT corner should be used (sideSign = 1)
        // If |distRight| < |distLeft|, the RIGHT corner should be used (sideSign = -1)
        const correctSideSign: 1 | -1 = Math.abs(distLeftToLine) < Math.abs(distRightToLine) ? 1 : -1;

        // Calculate hysteresis distance (perpendicular distance of dragged point from colinear line)
        const draggedToAnchor = draggedPosition.clone().sub(currentAnchorPoint);
        const perpDistance = Math.abs(draggedToAnchor.dot(targetPerp));

        // Check if we need to switch anchors
        const lastSign = pivotCache.lastRelativeSign ?? 0;
        const hasAlternate = !!pivotCache.alternateAnchor;


        // CRITICAL: Corner switching when near colinear
        // We check if the geometrically correct corner (based on which is closer to the
        // target centerline) differs from what we're currently using.
        const shouldSwitch = correctSideSign !== pivotCache.currentSideSign;

        if (hasAlternate && isNearColinear && shouldSwitch) {
            // We've crossed the colinear line - check hysteresis
            const hysteresisDistance = pivotCache.hysteresisDistance ?? 0;

            // Track accumulated distance past the colinear point
            if (perpDistance > hysteresisDistance) {
                pivotCache.hysteresisDistance = perpDistance;
            }

            // Only switch if we've traveled far enough past the colinear threshold
            if (perpDistance >= COLINEAR_HYSTERESIS_DISTANCE) {
                // Switch anchor points
                pivotCache.currentAnchor = pivotCache.currentAnchor === "primary" ? "alternate" : "primary";

                // Flip the side sign since we're now on the opposite corner
                pivotCache.currentSideSign = pivotCache.currentSideSign === 1 ? -1 : 1;

                // Reset hysteresis for the next potential switch
                pivotCache.hysteresisDistance = 0;
                pivotCache.lastRelativeSign = correctSideSign;
            }
        } else {
            // Always update the last known side for future comparisons
            // This ensures we have a valid baseline when entering the colinear zone
            pivotCache.lastRelativeSign = correctSideSign;

            // Reset hysteresis when moving away from colinear
            if (!isNearColinear) {
                pivotCache.hysteresisDistance = 0;
            }
        }

        // Select the anchor point based on current state
        const anchorPoint = pivotCache.currentAnchor === "primary"
            ? pivotCache.primaryAnchor
            : (pivotCache.alternateAnchor ?? pivotCache.primaryAnchor);

        const perpendicular = new THREE.Vector2(-directionNorm.y, directionNorm.x);

        // Use the current side sign (may have been flipped if we switched corners)
        const sideSign = pivotCache.currentSideSign;

        const offset = perpendicular.multiplyScalar(halfWidth * sideSign);

        if (anchorEndpoint === "start") {
            const nextStart = anchorPoint.clone().sub(offset);
            const delta = nextStart.clone().sub(nextPositions.start);
            nextPositions.start.copy(nextStart);
            // Keep the dragged end exactly where the user placed it
            nextPositions.end.copy(draggedEndpoint === "end" ? draggedPosition : nextPositions.end);
            if (delta.lengthSq() < convergenceEpsilon) {
                break;
            }
        } else {
            const nextEnd = anchorPoint.clone().sub(offset);
            const delta = nextEnd.clone().sub(nextPositions.end);
            nextPositions.end.copy(nextEnd);
            // Keep the dragged start exactly where the user placed it
            nextPositions.start.copy(draggedEndpoint === "start" ? draggedPosition : nextPositions.start);
            if (delta.lengthSq() < convergenceEpsilon) {
                break;
            }
        }
    }

    return nextPositions;
};
